import { FormEvent, useEffect, useState } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, TextField, Typography } from "@mui/material"
import AdminLayout from "../../../layouts/AdminLayout"
import AudioPlayer from "../../../components/AudioPlayer"
import { StatusChip } from "./Index"

type ConversationStatus = "draft" | "accepted" | "completed"

type Translation = { id: number; language: string; text: string }
type Speaker = { id: number; name: string; voice: string; gender: string | null }
type Audio = { url: string; language: string; ai_model: string; voice: string }

type Line = {
  id: number
  position: number
  speaker: Speaker
  sentence: {
    id: number
    kanji: string | null
    kana: string | null
    romaji: string | null
    translations: Translation[]
  }
  audio: Audio | null
}

type DraftWord = {
  kanji: string
  kana: string
  romaji: string
  word_type: string
  translations: { language: string; text: string; hint: string }[]
}

type DraftLine = {
  position: number
  speaker: string
  kanji: string
  kana: string
  romaji: string
  translations: { language: string; text: string; literal: string }[]
  words: DraftWord[]
}

type Conversation = {
  id: number
  title: string
  description: string | null
  status: ConversationStatus
  build_error: string | null
  topic: { id: number; name: string } | null
  line_count: number
  prompt?: string | null
  participants?: Speaker[]
  draft_lines?: DraftLine[]
  lines?: Line[]
}

const POLL_INTERVAL_MS = 4000

function DraftReview({ conversation }: { conversation: Conversation }) {
  const [feedback, setFeedback] = useState("")
  const [refining, setRefining] = useState(false)
  const [accepting, setAccepting] = useState(false)
  const lines = conversation.draft_lines ?? []
  const busy = refining || accepting

  const refine = (e: FormEvent) => {
    e.preventDefault()
    router.post(`/admin/conversations/${conversation.id}/refine`, { feedback },
      { onStart: () => setRefining(true), onFinish: () => setRefining(false) })
  }
  const accept = () => {
    router.post(`/admin/conversations/${conversation.id}/accept`, {},
      { onStart: () => setAccepting(true), onFinish: () => setAccepting(false) })
  }

  return (
    <>
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Draft dialogue ({lines.length} lines)</Typography>
      <Stack gap={2}>
        {lines.map((line, index) => (
          <Paper key={index} sx={{ p: 2 }} data-testid={`draft-line-${index}`}>
            <Stack direction="row" gap={1} sx={{ alignItems: "center", mb: 1 }}>
              <Chip size="small" label={`#${index + 1}`} />
              <Typography variant="subtitle2">{line.speaker}</Typography>
            </Stack>
            <Typography variant="h6">{line.kanji || line.kana}</Typography>
            <Typography variant="body2" color="text.secondary">{line.kana}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {line.romaji}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              {line.translations.map((t, ti) => (
                <Typography key={ti} variant="body2" color="text.secondary">
                  {t.language}: {t.text}{t.literal ? ` — (${t.literal})` : ""}
                </Typography>
              ))}
            </Box>
            {line.words.length > 0 && (
              <Stack direction="row" gap={1} sx={{ flexWrap: "wrap", mt: 1 }}>
                {line.words.map((w, wi) => (
                  <Chip key={wi} size="small" variant="outlined"
                        label={`${w.kanji || w.kana} · ${w.translations.find((t) => t.language === "en")?.text ?? ""}`} />
                ))}
              </Stack>
            )}
          </Paper>
        ))}
      </Stack>

      <Paper component="form" onSubmit={refine} sx={{ p: 2, mt: 3 }}>
        <Typography variant="subtitle1" gutterBottom>Refine the draft</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Describe a specific change — the AI keeps the rest of the dialogue intact.
        </Typography>
        <TextField
          fullWidth multiline rows={2} value={feedback} disabled={busy}
          onChange={(e) => setFeedback(e.target.value)}
          label="Feedback for the AI"
          placeholder="e.g. Make line 3 ask about the weather instead."
          slotProps={{ htmlInput: { "data-testid": "feedback" } }}
        />
        <Stack direction="row" gap={2} sx={{ mt: 2, alignItems: "center" }}>
          <Button type="submit" variant="outlined" disabled={!feedback.trim() || busy}
                  startIcon={refining ? <CircularProgress size={16} /> : undefined} data-testid="refine">
            {refining ? "Refining…" : "Refine draft"}
          </Button>
          <Button variant="contained" color="success" onClick={accept} disabled={busy}
                  startIcon={accepting ? <CircularProgress size={16} /> : undefined} data-testid="accept">
            {accepting ? "Accepting…" : "Accept & generate audio"}
          </Button>
        </Stack>
      </Paper>
    </>
  )
}

function MaterializedLines({ conversation }: { conversation: Conversation }) {
  const lines = conversation.lines ?? []

  // No ActionCable here — poll the page while the audio job runs so the lines
  // light up as their clips land, stopping once it completes or fails.
  useEffect(() => {
    if (conversation.status !== "accepted" || conversation.build_error) return
    const timer = setInterval(() => router.reload({ only: ["conversation"] }), POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [conversation.status, conversation.build_error])

  const rebuild = () => router.post(`/admin/conversations/${conversation.id}/rebuild`)

  return (
    <>
      {conversation.status === "accepted" && !conversation.build_error && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mt: 2 }} data-testid="building">
          Generating audio — this page refreshes automatically.
        </Alert>
      )}
      {conversation.build_error && (
        <Alert
          severity="error" sx={{ mt: 2 }} data-testid="build-error"
          action={<Button color="inherit" size="small" onClick={rebuild} data-testid="rebuild">Rebuild</Button>}
        >
          Audio generation failed: {conversation.build_error}
        </Alert>
      )}
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Lines ({lines.length})</Typography>
      {lines.length === 0 ? (
        <Typography color="text.secondary" data-testid="lines-empty">No lines yet.</Typography>
      ) : (
        <Stack gap={2}>
          {lines.map((line) => (
            <Paper key={line.id} sx={{ p: 2 }} data-testid={`line-${line.id}`}>
              <Stack direction="row" gap={1} sx={{ alignItems: "center", mb: 1 }}>
                <Chip size="small" label={`#${line.position + 1}`} />
                <Typography variant="subtitle2">{line.speaker.name}</Typography>
                <Typography variant="caption" color="text.secondary">voice: {line.speaker.voice}</Typography>
              </Stack>
              <Link href={`/admin/sentences/${line.sentence.id}`}>
                {line.sentence.kanji || line.sentence.kana}
              </Link>
              {line.sentence.romaji && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {line.sentence.romaji}
                </Typography>
              )}
              <Box sx={{ mt: 0.5 }}>
                {line.sentence.translations.map((t) => (
                  <Typography key={t.id} variant="body2" color="text.secondary">
                    {t.language}: {t.text}
                  </Typography>
                ))}
              </Box>
              <Box sx={{ mt: 1 }}>
                <AudioPlayer src={line.audio?.url ?? null} label="No audio generated yet" />
              </Box>
            </Paper>
          ))}
        </Stack>
      )}
    </>
  )
}

export default function ShowConversation({ conversation }: { conversation: Conversation }) {
  const { errors } = usePage<{ errors?: Record<string, string> }>().props

  return (
    <AdminLayout title={conversation.title}>
      <Head title={conversation.title} />
      <Stack direction="row" gap={1} sx={{ alignItems: "center", mb: 2 }}>
        <Typography variant="h5">{conversation.title}</Typography>
        <StatusChip status={conversation.status} />
      </Stack>
      {errors?.base && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="conversation-error">{errors.base}</Alert>
      )}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 1, alignItems: "start" }}>
          <Typography color="text.secondary">Description</Typography>
          <Typography>{conversation.description || "—"}</Typography>
          <Typography color="text.secondary">Topic</Typography>
          <Typography>{conversation.topic?.name ?? "—"}</Typography>
          {conversation.status === "draft" && (
            <>
              <Typography color="text.secondary">Scenario</Typography>
              <Typography>{conversation.prompt || "—"}</Typography>
              <Typography color="text.secondary">Participants</Typography>
              <Stack direction="row" gap={1} sx={{ flexWrap: "wrap" }} data-testid="participants">
                {(conversation.participants ?? []).map((s) => (
                  <Chip key={s.id} size="small"
                        label={s.gender ? `${s.name} (${s.gender})` : s.name} />
                ))}
              </Stack>
            </>
          )}
        </Box>
      </Paper>
      {conversation.status === "draft"
        ? <DraftReview conversation={conversation} />
        : <MaterializedLines conversation={conversation} />}
    </AdminLayout>
  )
}
